import { LopDetailNote } from "@/components/payslip/LopDetailNote";
import { useLocale } from "@/hooks/useLocale";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/utils/cn";
import type {
  DisputeLineDecision,
  IDispute,
  IDisputeLine,
} from "@/services/(company)/payrollDisputesApi";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const DECISION_LABEL: Record<DisputeLineDecision, string> = {
  PENDING: "company:payroll_disputes.decision_pending",
  ACCEPTED: "company:payroll_disputes.decision_accepted",
  REJECTED: "company:payroll_disputes.decision_rejected",
};

const DECISION_TONE: Record<DisputeLineDecision, string> = {
  PENDING: "bg-gray-100 text-gray-700",
  ACCEPTED: "bg-success-100/10 text-success-100",
  REJECTED: "bg-red-50 text-red-600",
};

/** Amounts go through `useLocale` (see CLAUDE.md) — `toLocaleString(undefined,
 *  …)` followed the *browser* locale, not the app's. */

interface IProps {
  dispute: IDispute;
}

export const DisputeDetailCard = ({ dispute }: IProps) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const { formatNumber } = useLocale();
  const formatAmount = (n: number) =>
    formatNumber(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
      {/* Left — main content */}
      <div className="rounded-xs border border-borderSecondary bg-white overflow-hidden">
        {/* Description */}
        <div className="px-5 py-4 border-b border-gray-100">
          <span className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
            {t("company:payroll_disputes.employee_description")}
          </span>
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {dispute.description}
          </p>
        </div>

        {/* Lines table */}
        <div className="px-5 py-3 bg-gray-50/50 border-b border-gray-100">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {t("company:payroll_disputes.proposed_line_changes_count", {
              count: dispute.lines.length,
            })}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-100">
                <th className="text-start px-5 py-2 font-medium">
                  {t("company:payroll_disputes.col_type")}
                </th>
                <th className="text-start px-2 py-2 font-medium">
                  {t("company:payroll_disputes.col_name")}
                </th>
                <th className="text-end px-2 py-2 font-medium">
                  {t("company:payroll_disputes.col_current")}
                </th>
                <th className="text-end px-2 py-2 font-medium">
                  {t("company:payroll_disputes.col_proposed")}
                </th>
                <th className="text-end px-2 py-2 font-medium">
                  {t("company:payroll_disputes.col_diff")}
                </th>
                <th className="text-start px-2 py-2 font-medium">
                  {t("company:payroll_disputes.col_note")}
                </th>
                <th className="text-start px-5 py-2 font-medium">
                  {t("company:payroll_disputes.col_decision")}
                </th>
              </tr>
            </thead>
            <tbody>
              {dispute.lines.map((l) => (
                <LineRow key={l.id} line={l} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Reviewer Response — comment-style */}
        {dispute.hrResponse && (
          <>
            <div className="px-5 py-3 bg-gray-50/50 border-y border-gray-100">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                {t("company:payroll_disputes.reviewer_response")}
              </span>
            </div>
            <div className="px-5 py-4">
              <div className="flex items-start gap-3">
                <Avatar className="size-8 shrink-0">
                  {dispute.resolvedByUser?.profileImage && (
                    <AvatarImage
                      src={dispute.resolvedByUser.profileImage}
                      alt={dispute.resolvedByUser.fullName}
                      className="object-cover"
                    />
                  )}
                  <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-bold">
                    {getInitials(dispute.resolvedByUser?.fullName ?? "HR")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-900">
                      {dispute.resolvedByUser?.fullName ?? "HR"}
                    </span>
                    {dispute.resolvedAt && (
                      <span className="text-[11px] text-gray-400">
                        {format(
                          new Date(dispute.resolvedAt),
                          "dd MMM yyyy · HH:mm",
                        )}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {dispute.hrResponse}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right — meta sidebar */}
      <aside className="rounded-xs border border-borderSecondary bg-white p-4 flex flex-col gap-3">
        <MetaItem
          label={t("company:payroll_disputes.raised_by")}
          value={dispute.raisedByUser?.fullName ?? "—"}
        />
        <MetaItem
          label={t("my-space:shared.category")}
          value={t(
            `company:payrolls.dispute_category_${dispute.category.toLowerCase()}`,
          )}
        />
        <MetaItem
          label={t("my-space:ui.pay_period")}
          value={
            dispute.payrollRun?.periodStart
              ? format(new Date(dispute.payrollRun.periodStart), "MMM yyyy")
              : "—"
          }
        />
        <MetaItem
          label={t("company:payroll_disputes.lines_proposed")}
          value={String(dispute.lines.length)}
        />
        <MetaItem
          label={t("my-space:ui.net_diff")}
          value={`${dispute.totalProposedDiff > 0 ? "+" : ""}${formatAmount(dispute.totalProposedDiff)}`}
          valueClass={cn(
            "font-semibold",
            dispute.totalProposedDiff > 0
              ? "text-success-100"
              : dispute.totalProposedDiff < 0
                ? "text-red-500"
                : "text-gray-900",
          )}
        />
        <MetaItem
          label={t("my-space:ui.raised_on")}
          value={format(new Date(dispute.createdAt), "dd MMM yyyy")}
        />
        {dispute.resolvedAt && (
          <MetaItem
            label={t("company:payroll_disputes.resolved_on")}
            value={format(new Date(dispute.resolvedAt), "dd MMM yyyy")}
          />
        )}
        {dispute.wpsSettledAt && (
          <div className="flex flex-col gap-0.5 pt-2 border-t border-gray-100">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
              {t("company:payroll_disputes.wps_settlement")}
            </span>
            <span className="text-sm font-semibold text-success-100">
              {t("company:payroll_disputes.settled")}
            </span>
            <span className="text-[11px] text-gray-500">
              {format(new Date(dispute.wpsSettledAt), "dd MMM yyyy · HH:mm")}
            </span>
            {dispute.wpsPaymentFileId && (
              <span className="text-[11px] text-gray-500">
                {t("company:payroll_disputes.payment_file_hash", {
                  id: dispute.wpsPaymentFileId,
                })}
              </span>
            )}
            {/* How it was settled (B47); absent on older settlements. */}
            {dispute.settlementMethod && (
              <span className="text-[11px] text-gray-700">
                {t(
                  `company:payroll_disputes.method_${dispute.settlementMethod.toLowerCase()}`,
                )}
                {dispute.settlementAmount !== null &&
                dispute.settlementAmount !== undefined &&
                dispute.settlementMethod !== "NO_PAYMENT_DUE"
                  ? ` · ${formatAmount(Math.abs(dispute.settlementAmount))}`
                  : ""}
              </span>
            )}
            {dispute.settlementReference && (
              <span className="text-[11px] text-gray-500">
                {t("company:payroll_disputes.reference_value", {
                  reference: dispute.settlementReference,
                })}
              </span>
            )}
            {dispute.settledByUser && (
              <span className="text-[11px] text-gray-500">
                {t("company:payroll_disputes.settled_by", {
                  name: dispute.settledByUser.fullName,
                })}
              </span>
            )}
            {dispute.settlementNote && (
              <span className="text-[11px] text-gray-500">
                {dispute.settlementNote}
              </span>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

const MetaItem = ({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
      {label}
    </span>
    <span className={cn("text-sm text-gray-900 capitalize", valueClass)}>
      {value}
    </span>
  </div>
);

const LineRow = ({ line }: { line: IDisputeLine }) => {
  const { t } = useTranslation(["common", "company", "my-space"]);
  const { formatNumber } = useLocale();
  const formatAmount = (n: number) =>
    formatNumber(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <tr className="border-b border-gray-50 last:border-b-0 align-top">
      <td className="px-5 py-2">
        <span className="text-[11px] font-medium text-gray-700">
          {t(
            `company:payroll_disputes.line_type_${line.lineType.toLowerCase()}`,
            {
              defaultValue: line.lineType,
            },
          )}
        </span>
      </td>
      <td className="px-2 py-2 font-medium text-gray-900">
        <span className="flex flex-col">
          {/* The server names Loss of Pay in English (B45). */}
          {line.lopDetail ? t("company:payrolls.loss_of_pay") : line.name}
          <LopDetailNote detail={line.lopDetail} />
        </span>
      </td>
      <td className="px-2 py-2 text-end text-gray-700 tabular-nums">
        {formatAmount(line.currentAmount)}
      </td>
      <td className="px-2 py-2 text-end text-gray-700 tabular-nums">
        {formatAmount(line.proposedAmount)}
      </td>
      <td
        className={cn(
          "px-2 py-2 text-end font-semibold tabular-nums",
          line.diff > 0
            ? "text-success-100"
            : line.diff < 0
              ? "text-red-500"
              : "text-gray-400",
        )}
      >
        {line.diff > 0 ? "+" : ""}
        {formatAmount(line.diff)}
      </td>
      <td className="px-2 py-2 text-gray-700 max-w-[200px]">
        {line.note || <span className="text-gray-300">—</span>}
        {line.hrNote && (
          <span className="block text-[11px] text-blue-700 mt-0.5">
            {t("company:payroll_disputes.reviewer_note", { note: line.hrNote })}
          </span>
        )}
      </td>
      <td className="px-5 py-2">
        <span
          className={cn(
            "text-[11px] font-semibold px-2 py-0.5 rounded-xs",
            DECISION_TONE[line.hrDecision],
          )}
        >
          {t(DECISION_LABEL[line.hrDecision])}
        </span>
      </td>
    </tr>
  );
};
